import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Modal, Typography } from '@mui/material';
import { PencilIcon } from 'lucide-react';
import apiClient from '../Services/ApiClient';
import apiClientFormData from '../Services/ApiClientFormData';
import { useSnackbar } from '../Context/SnackbarProvider';
import useGetData from '../Hooks/useGetData';
import useGetCustomData from '../Hooks/useGetCustomData';
import { decode } from '../Services/DecodeToken';
import { Kit, USER_ROLE } from '../Types';
import { COLORS } from '../Styles/Colors';
import { AddButton, CloseButton, EditButton, ModalHeader, ModalOverlay, ModalContent, PageContainer } from '../Styles/AppStyles';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { LocationSelect } from '../Components/Select/LocationSelect';
import { formatDatePayload } from '../Services/FormatDatePayload';
import { AssetEditForm, AssetFormInput } from './Assets/AssetEditForm';
import { KitEditForm, KitFormInput } from './Kits/KitEditForm';

export interface Asset {
  id: number;
  name: string;
  serial_number?: string;
  is_active: boolean;
  checked_out: boolean;
  needs_repair: boolean;
  needs_service: boolean;
  service_interval_months?: number;
  last_service_date?: string;
}

const KitsAndToolsPage: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddOptionsModal, setShowAddOptionsModal] = useState(false);
  const [addType, setAddType] = useState<'kit' | 'asset'>('kit');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedKit, setSelectedKit] = useState<Kit | null>(null);
  const [selectedOvs, setSelectedOvs] = useState<string>('');

  const { showSnackbar } = useSnackbar();
  const token: any = decode();
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [kits, loadingKit, , refetch] = useGetData<Kit>('/api/kits');

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && ovs.length > 0 && !selectedOvs) {
      setSelectedOvs(String(ovs[0].OVID));
    }
  }, [token.roles.id, ovs, selectedOvs]);

  const filteredKits = useMemo(() => {
    if (!kits || !Array.isArray(kits)) return [];
    if (!selectedOvs) return kits;
    return kits.filter((kit) => String(kit.OVID) === String(selectedOvs));
  }, [kits, selectedOvs]);

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedKit(null);
  };

  const handleAssetSubmit = async (formData: AssetFormInput) => {
    const assetPayload = {
      name: formData.name,
      serial_number: formData.serial_number || null,
      ovid: formData.ovid || null,
      barcode: formData.barcode || null,
      service_interval_months: formData.service_interval_months || null,
      last_service_date: formatDatePayload(formData.last_service_date ?? null),
      service_due_date: formatDatePayload(formData.service_due_date || null),
      category: formData.category || null,
      category_id: formData.category_id || null,
      manufacturer: formData.manufacturer || null,
      manufacturer_id: formData.manufacturer_id || null,
      part_number: formData.part_number || null,
      device_id: formData.device_id || null,
      bin_location_id: formData.bin_location_id || null,
      is_active: formData.is_active ?? true,
      checked_out: false,
      needs_repair: false,
      needs_service: false,
      cost: formData.cost ?? null,
      invoice_number: formData.invoice_number ?? null,
    };

    const form = new FormData();
    form.append('data', JSON.stringify(assetPayload));
    if (formData.image) {
      form.append('image', formData.image);
    }

    try {
      setSubmitLoading(true);
      const response = await apiClientFormData('/api/assets', { method: 'POST', body: form });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showSnackbar(errorData.error || 'Failed to add asset', 'error', 'Something went wrong!');
        return;
      }

      const newAsset = await response.json();
      showSnackbar(`${newAsset.name} is successfully added.`, 'success', 'Successfully Added!');
      handleCloseModal();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleKitSubmit = async (formData: KitFormInput) => {
    const sanitizedAssets = (formData.assets || []).map((asset) => {
      const { image, image_url, ...rest } = asset as Asset & { image?: unknown; image_url?: unknown };
      return rest;
    });

    const payload = {
      name: formData.name,
      description: formData.description,
      OVID: formData.OVID,
      active_flag: formData.active_flag ? 1 : 0,
      assets: sanitizedAssets,
    };

    const kitId = selectedKit?.id ?? selectedKit?.kit_id;
    const url = kitId ? `/api/kits/${kitId}` : '/api/kits';
    const method = kitId ? 'PUT' : 'POST';

    try {
      setSubmitLoading(true);
      const response = await apiClient(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showSnackbar(errorData.error || 'Failed to save kit', 'error', 'Something went wrong!');
        return;
      }

      showSnackbar(
        kitId ? `${formData.name} is successfully updated.` : `${formData.name} is successfully added.`,
        'success',
        kitId ? 'Successfully Updated!' : 'Successfully Added!'
      );
      refetch();
      handleCloseModal();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAddOptionSelect = (type: 'kit' | 'asset') => {
    setSelectedKit(null);
    setAddType(type);
    setShowAddOptionsModal(false);
    setShowAddModal(true);
  };

  const kitColumnsNew: ColumnDef<any>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <EditButton
            onClick={() => {
              setSelectedKit(params.row);
              setAddType('kit');
              setShowAddModal(true);
            }}
          >
            <PencilIcon />
          </EditButton>
        </div>
      ),
    },
    { field: 'name', headerName: 'Kit Name', width: 200, sortable: true, filterable: true, searchable: true },
    { field: 'description', headerName: 'Description', width: 300, sortable: true, filterable: true, searchable: true },
    { field: 'location_name', headerName: 'Location', width: 150, sortable: true, filterable: true, searchable: true },
    { field: 'created_date', headerName: 'Created Date', width: 150, sortable: true },
    { field: 'active_flag', headerName: 'Active', width: 100, type: 'boolean' },
    {
      field: 'tools',
      headerName: 'Tools',
      width: 900,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, width: '100%', height: '100%' }}>
          {(params.row.assets || []).map((asset: Asset) => (
            <Chip key={asset.id} label={asset.name} size="small" sx={{ backgroundColor: COLORS.teal, color: '#fff' }} />
          ))}
        </Box>
      ),
    },
  ];

  const kitFormDefaults: KitFormInput | undefined = selectedKit
    ? {
        name: selectedKit.name,
        description: selectedKit.description,
        OVID: String(selectedKit.OVID),
        active_flag: Boolean(selectedKit.active_flag),
        assets: selectedKit.assets ?? [],
      }
    : undefined;

  return (
    <PageContainer>
      <Header title="Kits and Tools" count={filteredKits.length} hideCount={false}>
        <AddButton onClick={() => setShowAddOptionsModal(true)}>+</AddButton>
      </Header>

      <Modal open={showAddOptionsModal} onClose={() => setShowAddOptionsModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            borderRadius: 2,
            width: 'calc(100% - 32px)',
            maxWidth: 400,
            maxHeight: '90dvh',
            overflowY: 'auto',
            p: 3,
            boxShadow: 24,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={() => setShowAddOptionsModal(false)}>×</CloseButton>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            What would you like to add?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => handleAddOptionSelect('kit')}
              sx={{ backgroundColor: COLORS.teal, '&:hover': { backgroundColor: COLORS.darkTeal }, py: 2 }}
            >
              Add Kit
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddOptionSelect('asset')}
              sx={{
                borderColor: COLORS.teal,
                color: COLORS.teal,
                '&:hover': { borderColor: COLORS.teal, backgroundColor: `${COLORS.teal}10` },
                py: 2,
              }}
            >
              Add Tool
            </Button>
          </Box>
        </Box>
      </Modal>

      {showAddModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860 }}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ModalHeader>
              {addType === 'asset' ? 'Add Tool' : selectedKit ? 'Edit Kit' : 'Add Kit'}
            </ModalHeader>
            {addType === 'asset' ? (
              <AssetEditForm
                key="add-asset"
                initialOvid={selectedOvs || undefined}
                hideLocation={!!selectedOvs}
                submitLabel="Add Tool"
                onSubmit={handleAssetSubmit}
                onCancel={handleCloseModal}
              />
            ) : (
              <KitEditForm
                key={selectedKit ? `edit-kit-${selectedKit.id ?? selectedKit.kit_id}` : 'new-kit'}
                isEditing={!!selectedKit}
                isLoading={submitLoading}
                initialOvid={selectedOvs || undefined}
                onSubmit={handleKitSubmit}
                onCancel={handleCloseModal}
                defaultValues={kitFormDefaults}
              />
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      <DataTable data={filteredKits} columns={kitColumnsNew} loading={loadingKit} pageKey="kits">
        <LocationSelect
          ovs={ovs || []}
          selectedOvs={selectedOvs}
          onChange={setSelectedOvs}
          defaultName="All Locations"
        />
      </DataTable>
    </PageContainer>
  );
};

export default KitsAndToolsPage;
